import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "../assets/css/sidebar.css";

const Sidebar = ({ onMenuClick, isVisible, user }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1200);
  const [openMenus, setOpenMenus] = useState({});

  const basePath = "/crm";

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1200);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toggleMenu = (key) => {
    setOpenMenus((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleMenuItemClick = () => {
    if (isMobile) onMenuClick();
    setOpenMenus({});
  };

  // HIPAA-safe:
  if (!user) return null;

  // User role and type checks
  const isAdmin = user?.is_admin === true;
  const tenantType = user?.tenant_type?.toLowerCase() || "";
  const roleLower = user?.role?.toLowerCase() || "";
  const isPrimaryTenant = user?.is_primary === true;

  // Determine what to show based on tenant type and role
  const isVendor = tenantType === "vendor";
  const isCustomer = tenantType === "customer";
  const isJobCoach = roleLower === "job coach";
  const isCounselor = roleLower === "counselor";
  const hasPlacementAccess = user?.has_placement_access === true;

  return (
    <div className={`sidebar ${isVisible ? "visible" : "hidden"}`}>
      <div className="p-3 text-center border-bottom">
        <Link
          to="/crm/dashboard"
          className="text-white text-decoration-none h4 mb-0"
        >
          WebVenly CRP
        </Link>
      </div>

      <ul className="list-unstyled p-3">
        {/* Dashboard - Everyone sees this */}
        <li>
          <Link
            to={`${basePath}/dashboard`}
            className="btn btn-link text-white w-100 text-start"
            onClick={handleMenuItemClick}
          >
            <i className="bi bi-speedometer2 me-2"></i> Dashboard
          </Link>
        </li>

        {/* Profile - Everyone sees this */}
        <li>
          <Link
            to={`${basePath}/profile`}
            className="btn btn-link text-white w-100 text-start"
            onClick={handleMenuItemClick}
          >
            <i className="bi bi-person-circle me-2"></i> Profile
          </Link>
        </li>

        {/* ===================================================== */}
        {/* Admin Section - Only Admins */}
        {/* ===================================================== */}
        {isAdmin && (
          <li>
            <button
              className="btn btn-link text-white w-100 text-start fw-bold"
              onClick={() => toggleMenu("admin")}
            >
              Admin
            </button>
            {openMenus.admin && (
              <ul className="list-unstyled ps-1">
                {/* Account Setup */}
                <li>
                  <button
                    className="btn btn-link text-white w-100 text-start"
                    onClick={() => toggleMenu("account")}
                  >
                    Account Setup
                  </button>
                  {openMenus.account && (
                    <ul className="list-unstyled ps-1">
                      <li>
                        <Link
                          to={`${basePath}/TenantInfoForm`}
                          className="btn btn-link text-white w-100 text-start"
                          onClick={handleMenuItemClick}
                        >
                          Tenant
                        </Link>
                      </li>
                      <li>
                        <Link
                          to={`${basePath}/user`}
                          className="btn btn-link text-white w-100 text-start"
                          onClick={handleMenuItemClick}
                        >
                          User
                        </Link>
                      </li>
                      <li>
                        <Link
                          to={`${basePath}/assign-visibility`}
                          className="btn btn-link text-white w-100 text-start"
                          onClick={handleMenuItemClick}
                        >
                          Assign Visibility
                        </Link>
                      </li>
                      {isPrimaryTenant && (
                        <li>
                          <Link
                            to={`${basePath}/tenants`}
                            className="btn btn-link text-white w-100 text-start"
                            onClick={handleMenuItemClick}
                          >
                            Tenant Management
                          </Link>
                        </li>
                      )}
                    </ul>
                  )}
                </li>

                {/* System Setup */}
                <li>
                  <button
                    className="btn btn-link text-white w-100 text-start"
                    onClick={() => toggleMenu("system")}
                  >
                    System Setup
                  </button>
                  {openMenus.system && (
                    <ul className="list-unstyled ps-1">
                      {isPrimaryTenant && (
                        <li>
                          <Link
                            to={`${basePath}/roles`}
                            className="btn btn-link text-white w-100 text-start"
                            onClick={handleMenuItemClick}
                          >
                            Roles
                          </Link>
                        </li>
                      )}
                      <li>
                        <Link
                          to={`${basePath}/services`}
                          className="btn btn-link text-white w-100 text-start"
                          onClick={handleMenuItemClick}
                        >
                          Services
                        </Link>
                      </li>
                      <li>
                        <Link
                          to={`${basePath}/milestone-templates`}
                          className="btn btn-link text-white w-100 text-start"
                          onClick={handleMenuItemClick}
                        >
                          Milestone Templates
                        </Link>
                      </li>
                      {/* Branch/Office Management - All Admins */}
                      <li>
                        <Link
                          to={`${basePath}/branches`}
                          className="btn btn-link text-white w-100 text-start"
                          onClick={handleMenuItemClick}
                        >
                          Branch/Office
                        </Link>
                      </li>
                      {isPrimaryTenant && (
                        <>
                          <li>
                            <Link
                              to={`${basePath}/StatusPage`}
                              className="btn btn-link text-white w-100 text-start"
                              onClick={handleMenuItemClick}
                            >
                              Status
                            </Link>
                          </li>
                          <li>
                            <Link
                              to={`${basePath}/StatesPage`}
                              className="btn btn-link text-white w-100 text-start"
                              onClick={handleMenuItemClick}
                            >
                              States
                            </Link>
                          </li>
                          <li>
                            <Link
                              to={`${basePath}/announcements`}
                              className="btn btn-link text-white w-100 text-start"
                              onClick={handleMenuItemClick}
                            >
                              <i className="bi bi-megaphone me-2"></i>
                              Announcements
                            </Link>
                          </li>
                          <li>
                            <Link
                              to={`${basePath}/version`}
                              className="btn btn-link text-white w-100 text-start"
                              onClick={handleMenuItemClick}
                            >
                              <i className="bi bi-git me-2"></i>
                              Version Management
                            </Link>
                          </li>
                        </>
                      )}
                      {/* Follow-up Stages Config - Admin only */}
                      <li>
                        <Link
                          to={`${basePath}/followup-stages`}
                          className="btn btn-link text-white w-100 text-start"
                          onClick={handleMenuItemClick}
                        >
                          Follow-up Stages
                        </Link>
                      </li>
                    </ul>
                  )}
                </li>

                {/* Compliance & Security */}
                <li>
                  <button
                    className="btn btn-link text-white w-100 text-start"
                    onClick={() => toggleMenu("compliance")}
                  >
                    Compliance & Security
                  </button>
                  {openMenus.compliance && (
                    <ul className="list-unstyled ps-1">
                      <li>
                        <Link
                          to={`${basePath}/audit-logs`}
                          className="btn btn-link text-white w-100 text-start"
                          onClick={handleMenuItemClick}
                        >
                          <i className="bi bi-shield-check me-2"></i>
                          Audit Logs
                        </Link>
                      </li>
                      <li>
                        <Link
                          to={`${basePath}/user-login-tracking`}
                          className="btn btn-link text-white w-100 text-start"
                          onClick={handleMenuItemClick}
                        >
                          <i className="bi bi-person-badge me-2"></i>
                          User Login Tracking
                        </Link>
                      </li>
                    </ul>
                  )}
                </li>
              </ul>
            )}
          </li>
        )}

        {/* ===================================================== */}
        {/* Client Management - Admin Only */}
        {/* ===================================================== */}
        {isAdmin && (
          <li>
            <button
              className="btn btn-link text-white w-100 text-start fw-bold"
              onClick={() => toggleMenu("clientMgmt")}
            >
              Client Management
            </button>
            {openMenus.clientMgmt && (
              <ul className="list-unstyled ps-1">
                <li>
                  <Link
                    to={`${basePath}/customers`}
                    className="btn btn-link text-white w-100 text-start"
                    onClick={handleMenuItemClick}
                  >
                    Customers
                  </Link>
                </li>
                <li>
                  <Link
                    to={`${basePath}/counselors`}
                    className="btn btn-link text-white w-100 text-start"
                    onClick={handleMenuItemClick}
                  >
                    Counselors
                  </Link>
                </li>
                <li>
                  <Link
                    to={`${basePath}/consumers`}
                    className="btn btn-link text-white w-100 text-start"
                    onClick={handleMenuItemClick}
                  >
                    Consumers
                  </Link>
                </li>
                <li>
                  <Link
                    to={`${basePath}/job-coach-assignments`}
                    className="btn btn-link text-white w-100 text-start"
                    onClick={handleMenuItemClick}
                  >
                    Assignments
                  </Link>
                </li>
              </ul>
            )}
          </li>
        )}

        {/* ===================================================== */}
        {/* Record Management - Admin Only */}
        {/* ===================================================== */}
        {isAdmin && (
          <li>
            <button
              className="btn btn-link text-white w-100 text-start fw-bold"
              onClick={() => toggleMenu("recordMgmt")}
            >
              Record Management
            </button>
            {openMenus.recordMgmt && (
              <ul className="list-unstyled ps-1">
                <li>
                  <Link
                    to={`${basePath}/authorizations`}
                    className="btn btn-link text-white w-100 text-start"
                    onClick={handleMenuItemClick}
                  >
                    Authorization
                  </Link>
                </li>
                <li>
                  <button
                    className="btn btn-link text-white w-100 text-start d-flex align-items-center justify-content-between"
                    onClick={() => toggleMenu("paymentReview")}
                  >
                    <span>
                      <i className="bi bi-credit-card me-2"></i>
                      Payment Review
                    </span>
                    <i className="bi bi-chevron-right"></i>
                  </button>
                  {openMenus.paymentReview && (
                    <ul className="list-unstyled ps-1">
                      <li>
                        <Link
                          to={`${basePath}/job-log-review`}
                          className="btn btn-link text-white w-100 text-start"
                          onClick={handleMenuItemClick}
                        >
                          Job Log Review
                        </Link>
                      </li>
                      <li>
                        <Link
                          to={`${basePath}/mileage-review`}
                          className="btn btn-link text-white w-100 text-start"
                          onClick={handleMenuItemClick}
                        >
                          Mileage Review
                        </Link>
                      </li>
                      <li>
                        <Link
                          to={`${basePath}/payment-review`}
                          className="btn btn-link text-white w-100 text-start"
                          onClick={handleMenuItemClick}
                        >
                          Payment Review
                        </Link>
                      </li>
                    </ul>
                  )}
                </li>
                <li>
                  <button
                    className="btn btn-link text-white w-100 text-start d-flex align-items-center justify-content-between"
                    onClick={() => toggleMenu("invoice")}
                  >
                    <span>
                      <i className="bi bi-receipt me-2"></i>
                      Invoice
                    </span>
                    <i className="bi bi-chevron-right"></i>
                  </button>
                  {openMenus.invoice && (
                    <ul className="list-unstyled ps-1">
                      <li>
                        <Link
                          to={`${basePath}/invoices`}
                          className="btn btn-link text-white w-100 text-start"
                          onClick={handleMenuItemClick}
                        >
                          Invoice Prep
                        </Link>
                      </li>
                      <li>
                        <Link
                          to={`${basePath}/posted-invoices`}
                          className="btn btn-link text-white w-100 text-start"
                          onClick={handleMenuItemClick}
                        >
                          Posted Invoices
                        </Link>
                      </li>
                    </ul>
                  )}
                </li>
                <li>
                  <Link
                    to={`${basePath}/documents`}
                    className="btn btn-link text-white w-100 text-start"
                    onClick={handleMenuItemClick}
                  >
                    Document Center
                  </Link>
                </li>
                <li>
                  <Link
                    to={`${basePath}/reports/cost-analysis`}
                    className="btn btn-link text-white w-100 text-start"
                    onClick={handleMenuItemClick}
                  >
                    <i className="bi bi-bar-chart-line me-2"></i>
                    Reports
                  </Link>
                </li>
              </ul>
            )}
          </li>
        )}

        {/* ===================================================== */}
        {/* Job Processing - Vendor Admins and Job Coaches */}
        {/* ===================================================== */}
        {isVendor && (isAdmin || isJobCoach) && (
          <li>
            <button
              className="btn btn-link text-white w-100 text-start fw-bold"
              onClick={() => toggleMenu("job")}
            >
              Job Processing
            </button>
            {openMenus.job && (
              <ul className="list-unstyled ps-1">
                <li>
                  <button
                    className="btn btn-link text-white w-100 text-start"
                    onClick={() => toggleMenu("forms")}
                  >
                    Forms
                  </button>
                  {openMenus.forms && (
                    <ul className="list-unstyled ps-1">
                      <li>
                        <Link
                          to={`${basePath}/plans`}
                          className="btn btn-link text-white w-50 text-start sidebar-link-stacked"
                          onClick={handleMenuItemClick}
                        >
                          <span className="crp-code">CRP-20</span>
                          <span className="text-small">CRP Service Plan</span>
                        </Link>
                      </li>
                      <li>
                        <Link
                          to={`${basePath}/community-based-assessment`}
                          className="btn btn-link text-white w-50 text-start sidebar-link-stacked"
                          onClick={handleMenuItemClick}
                        >
                          <span className="crp-code">CRP-21 </span>
                          <span className="text-small">Community Based Assessment</span>
                        </Link>
                      </li>
                      <li>
                        <Link
                          to={`${basePath}/monthly-reports`}
                          className="btn btn-link text-white w-50 text-start sidebar-link-stacked"
                          onClick={handleMenuItemClick}
                        >
                          <span className="crp-code">CRP-22</span>
                          <span className="text-small">
                            CRP Progress Report
                          </span>
                        </Link>
                      </li>
                      {hasPlacementAccess && (
                        <li>
                          <Link
                            to={`${basePath}/job-placement`}
                            className="btn btn-link text-white w-50 text-start sidebar-link-stacked"
                            onClick={handleMenuItemClick}
                          >
                            <span className="crp-code">CRP-30</span>
                            <span className="text-small">Job Placement Plan</span>
                          </Link>
                        </li>
                      )}
                    </ul>
                  )}
                </li>
                <li>
                  <Link
                    to={`${basePath}/locations`}
                    className="btn btn-link text-white w-100 text-start"
                    onClick={handleMenuItemClick}
                  >
                    Locations
                  </Link>
                </li>
                <li>
                  <Link
                    to={`${basePath}/mileage-logs`}
                    className="btn btn-link text-white w-100 text-start"
                    onClick={handleMenuItemClick}
                  >
                    Mileage Route
                  </Link>
                </li>
                <li>
                  <Link
                    to={`${basePath}/job-logs`}
                    className="btn btn-link text-white w-100 text-start"
                    onClick={handleMenuItemClick}
                  >
                    Job Log
                  </Link>
                </li>
              </ul>
            )}
          </li>
        )}

        {/* ===================================================== */}
        {/* Placement Management - Vendor users with placement access */}
        {/* ===================================================== */}
        {isVendor && hasPlacementAccess && (
          <li>
            <button
              className="btn btn-link text-white w-100 text-start fw-bold"
              onClick={() => toggleMenu("placement")}
            >
              Placement Management
            </button>
            {openMenus.placement && (
              <ul className="list-unstyled ps-1">
                {/* Contacts (Employers) */}
                <li>
                  <Link
                    to={`${basePath}/contacts`}
                    className="btn btn-link text-white w-100 text-start"
                    onClick={handleMenuItemClick}
                  >
                    
                    Contacts
                  </Link>
                </li>

                {/* Outreach (Pre-Placement) */}
                <li>
                  <Link
                    to={`${basePath}/placement-outreach`}
                    className="btn btn-link text-white w-100 text-start"
                    onClick={handleMenuItemClick}
                  >
                   
                    Outreach
                  </Link>
                </li>

                {/* CRP-33 Job Search Data Sheet */}
                <li>
                  <Link
                    to={`${basePath}/job-search`}
                    className="btn btn-link text-white w-100 text-start sidebar-link-stacked"
                    onClick={handleMenuItemClick}
                  >
                    <span className="crp-code">CRP-33</span>
                    <span className="text-small">Job Search Data Sheet</span>
                  </Link>
                </li>

                {/* Placements */}
                <li>
                  <Link
                    to={`${basePath}/placements`}
                    className="btn btn-link text-white w-100 text-start"
                    onClick={handleMenuItemClick}
                  >
                    
                    Placements
                  </Link>
                </li>

                {/* Retention (Post-Placement) */}
                <li>
                  <Link
                    to={`${basePath}/placement-retention`}
                    className="btn btn-link text-white w-100 text-start"
                    onClick={handleMenuItemClick}
                  >
                    
                    Retention
                  </Link>
                </li>
              </ul>
            )}
          </li>
        )}

        {/* My Consumers - Everyone sees this */}
        <li>
          <Link
            to={`${basePath}/my-consumers`}
            className="btn btn-link text-white w-100 text-start"
            onClick={handleMenuItemClick}
          >
            <i className="bi bi-people me-2"></i> My Consumers
          </Link>
        </li>

        {/* Customer Reports - Customer Admins and Managers Only */}
        {isCustomer && (isAdmin || roleLower === "manager") && (
          <li>
            <button
              className="btn btn-link text-white w-100 text-start fw-bold"
              onClick={() => toggleMenu("customerReports")}
            >
              <i className="bi bi-graph-up me-2"></i> Customer Reports
            </button>
            {openMenus.customerReports && (
              <ul className="list-unstyled ps-1">
                <li>
                  <Link
                    to={`${basePath}/counselor-activity-report`}
                    className="btn btn-link text-white w-100 text-start d-flex align-items-center justify-content-between"
                    onClick={handleMenuItemClick}
                  >
                    <span>
                      <i className="bi bi-file-earmark-spreadsheet me-2"></i>
                      Counselor Activity Report
                    </span>
                    <span className="badge bg-info text-dark ms-2">CSV</span>
                  </Link>
                </li>
              </ul>
            )}
          </li>
        )}

        {/* Policy and Protocol - Everyone sees this */}
        <li>
          <Link
            to={`${basePath}/policies`}
            className="btn btn-link text-white w-100 text-start"
            onClick={handleMenuItemClick}
          >
            <i className="bi bi-file-earmark-text me-2"></i> Policy and Protocol
          </Link>
        </li>

        {/* Announcements - Everyone sees this */}
        <li>
          <Link
            to={`${basePath}/announcement-history`}
            className="btn btn-link text-white w-100 text-start"
            onClick={handleMenuItemClick}
          >
            <i className="bi bi-clock-history me-2"></i> Announcements
          </Link>
        </li>

        {/* Tickets - Everyone sees this */}
        <li>
          <Link
            to={`${basePath}/tickets`}
            className="btn btn-link text-white w-100 text-start"
            onClick={handleMenuItemClick}
          >
            <i className="bi bi-ticket me-2"></i> Support Tickets
          </Link>
        </li>
      </ul>
    </div>
  );
};

export default Sidebar;